/**
 * FinishTriggerSystem - Pure logic system for level finish trigger management.
 *
 * Manages the finish trigger state (inactive/active), position, and collision
 * checking. The trigger activates when all 5 caves are complete (checked externally
 * by GameScene) and detects when the player overlaps the trigger zone.
 *
 * Requirements: 3.1, 3.4, 3.5
 */

// ─── Constants ────────────────────────────────────────────────────────────────

/** X position of the finish trigger (near end of 25000px world). */
const FINISH_TRIGGER_X = 24500;

/** Y position of the finish trigger (ground level). */
const GROUND_Y = 656;

/** Width of the trigger collision zone (matches portal sprite size). */
const TRIGGER_WIDTH = 128;

/** Height of the trigger collision zone (matches portal sprite size). */
const TRIGGER_HEIGHT = 128;

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Simple rectangle bounds for collision checking.
 * Avoids direct Phaser.Geom.Rectangle dependency — any object with these fields works.
 */
export interface RectangleBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Interface for the FinishTriggerSystem, enabling testability and decoupling from Phaser.
 */
export interface IFinishTriggerSystem {
  readonly isActive: boolean;
  readonly position: { x: number; y: number };

  activate(): void;
  deactivate(): void;
  checkCollision(playerBounds: RectangleBounds): boolean;
}

// ─── Implementation ───────────────────────────────────────────────────────────

/**
 * FinishTriggerSystem implementation.
 * Pure logic — no Phaser dependencies. Collision uses simple AABB overlap.
 *
 * State machine: inactive → active (via activate())
 * - Inactive: checkCollision always returns false
 * - Active: checkCollision returns true if player bounds overlap trigger zone
 */
export class FinishTriggerSystem implements IFinishTriggerSystem {
  private active: boolean = false;

  private readonly triggerPosition: { x: number; y: number } = {
    x: FINISH_TRIGGER_X,
    y: GROUND_Y,
  };

  // ─── State Queries ──────────────────────────────────────────────────────────

  /**
   * Whether the finish trigger is currently active (collision checking enabled).
   */
  get isActive(): boolean {
    return this.active;
  }

  /**
   * The world position of the finish trigger.
   */
  get position(): { x: number; y: number } {
    return { x: this.triggerPosition.x, y: this.triggerPosition.y };
  }

  // ─── Actions ────────────────────────────────────────────────────────────────

  /**
   * Activate the finish trigger, enabling collision checking.
   * Called when all 5 caves in the world are complete.
   */
  activate(): void {
    this.active = true;
  }

  /**
   * Deactivate the finish trigger, disabling collision checking.
   */
  deactivate(): void {
    this.active = false;
  }

  // ─── Collision ──────────────────────────────────────────────────────────────

  /**
   * Check if the player bounds overlap with the trigger zone.
   * Returns true only when the trigger is active AND bounds overlap.
   *
   * Uses AABB (Axis-Aligned Bounding Box) overlap detection.
   */
  checkCollision(playerBounds: RectangleBounds): boolean {
    if (!this.active) {
      return false;
    }

    // Trigger bounds: centered at position (x, y) with TRIGGER_WIDTH × TRIGGER_HEIGHT
    const triggerLeft = this.triggerPosition.x - TRIGGER_WIDTH / 2;
    const triggerRight = this.triggerPosition.x + TRIGGER_WIDTH / 2;
    const triggerTop = this.triggerPosition.y - TRIGGER_HEIGHT / 2;
    const triggerBottom = this.triggerPosition.y + TRIGGER_HEIGHT / 2;

    // Player bounds: x, y represent top-left corner
    const playerLeft = playerBounds.x;
    const playerRight = playerBounds.x + playerBounds.width;
    const playerTop = playerBounds.y;
    const playerBottom = playerBounds.y + playerBounds.height;

    // AABB overlap check: no overlap if any gap exists
    const noOverlap =
      playerRight <= triggerLeft ||
      playerLeft >= triggerRight ||
      playerBottom <= triggerTop ||
      playerTop >= triggerBottom;

    return !noOverlap;
  }
}
